import React, { useState, useEffect, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { mediaUrl, useAuth } from '../auth';
import { useSingleNotif } from '../components/SingleNotif';
import { IconSignal, IconBuilding, IconSpinner } from '../components/Icons';
import VodafoneBadge from '../components/VodafoneBadge';
import AudioPlayer from './AudioPlayer';
import { MobileTheme } from './mobileTheme';
import { FilterBarContext } from './filterBar';

const LUCCHETTO_M = {
  GRIGIO: '#9ca3af', VERDE: '#22c55e', NERO: '#111827', VIOLA: '#a855f7',
  ROSSO: '#ef4444', GIALLO: '#eab308', ARANCIONE: '#f97316', 'NON TROVATO': '#94a3b8',
};
const cfValido = (cf) => { const v = (cf || '').trim().toUpperCase(); return /^[0-9]{11}$/.test(v) || /^[A-Z0-9]{16}$/.test(v); };

const PlaySmall = () => (<svg viewBox="0 0 24 24" width="20" height="20"><path d="M8 5.5v13l11-6.5z" fill="currentColor" /></svg>);
const PhoneIcon = () => (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.3 1.1.4 2.4.6 3.6.6.6 0 1 .5 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.3 1.1l-2.2 2.1z" fill="currentColor" /></svg>);
const XIcon = () => (<svg viewBox="0 0 24 24" width="17" height="17"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>);
const FunnelIcon = () => (<svg viewBox="0 0 24 24" width="17" height="17"><path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>);

const nf = (n) => (n || 0).toLocaleString('it-IT');
const TIPI = [['', 'Tutti'], ['azienda', 'Azienda'], ['privato', 'Privato']];
const TECS = [
  ['', 'Tutte'], ['FTTH:10000', 'FTTH 10G'], ['FTTH:2500', 'FTTH 2,5G'], ['FTTH:1000', 'FTTH 1G'],
  ['FTTH', 'FTTH'], ['FTTC', 'FTTC'], ['FWA', 'FWA'], ['ADSL', 'ADSL'],
];
const ESITI_COP = [['', 'Tutti'], ['COPERTO', 'Coperto'], ['NON CENSITO', 'Non censito'], ['__none__', 'Non verif.']];

function techClass(t) { return t === 'FTTH' ? 'ftth' : 'fttc'; }
function techLabel(t) { return t ? (t === 'FTTH' ? 'FTTH' : t === 'FTTC' ? 'FTTC' : t) : null; }
const esitoColor = (r) => (r === 'NON TIM' ? 'var(--ok)' : r === 'TIM' ? 'var(--alert)' : 'var(--muted2)');
// "da fare" = mai verificato OPPURE oltre il timing (come le icone-spia desktop)
const _scaduto = (d, days) => { if (!d) return true; return new Date(d).getTime() < (Date.now() - (days || 180) * 86400000); };

function Dettaglio({ n, onClose, onChanged, coperturaOn, timings }) {
  const { theme, mode } = useContext(MobileTheme);
  const { can } = useAuth();
  const { start, done } = useSingleNotif();
  const [cur, setCur] = useState(n);
  const [busy, setBusy] = useState(null);     // '456' | 'cop' | 'ssm'
  const [play, setPlay] = useState(false);
  const nome = cur.ragione_sociale || 'Senza nome';
  const esito = cur.risultato;
  const esitoVar = esito === 'NON TIM' ? 'var(--ok)' : esito === 'TIM' ? 'var(--signal)' : 'var(--muted)';
  const label = cur.numero_pulito || nome;
  const tm = timings || { v456: 180, cop: 90, ssm: 90, ssmEnabled: true };
  const da456 = !cur.risultato || _scaduto(cur.verified_at, tm.v456);
  const daCop = !cur.copertura_esito || _scaduto(cur.copertura_at, tm.cop);
  const cfOk = cfValido(cur.codice_fiscale);
  const daSsm = cfOk && (!cur.ssmate_lucchetto || (tm.ssmEnabled && _scaduto(cur.ssmate_verificato_at, tm.ssm)));
  const daGea = cur.risultato === 'NON TIM' && (cur.numero_pulito || '').startsWith('0')
    && (!cur.gea_risultato || cur.gea_risultato === 'ERRORE' || _scaduto(cur.gea_data_verifica, tm.gea));

  const verifyGea = async () => {
    setBusy('gea'); const id = start('gea', label);
    try {
      const r = await axios.post(`${API}/gea/verifica/${cur.id}`);
      if (r.data.skipped) done(id, r.data.motivo || 'Saltato', '#b45309');
      else if (r.data.disservizio) done(id, 'Portale Vodafone in disservizio', '#b45309');
      else {
        const ris = r.data.risultato;
        setCur((c) => ({ ...c, gea_risultato: ris, gea_numero: r.data.numero_verificato, gea_tipo: r.data.gea_tipo, gestore_rete_fissa: r.data.gestore_rete_fissa }));
        done(id, ris === 'VODAFONE' ? 'Vodafone' : (ris === 'NEGATIVO' ? 'Non Vodafone' : ris), ris === 'VODAFONE' ? '#e60000' : '#64748b');
        onChanged?.();
      }
    } catch (e) { done(id, e.response?.data?.detail || 'Errore', '#ef4444'); }
    setBusy(null);
  };
  const verify456 = async () => {
    setBusy('456'); const id = start('456', label);
    try {
      const r = await axios.post(`${API}/verify-single`, { numero_id: cur.id });
      const ris = r.data.risultato;
      setCur((c) => ({ ...c, risultato: ris, numero_verificato: r.data.numero_verificato, audio_file: r.data.audio_file || c.audio_file }));
      done(id, ris === 'TIM' ? 'TIM (Telecom)' : ris === 'NON TIM' ? 'NON TIM' : (ris || 'esito'),
        ris === 'NON TIM' ? '#16a34a' : ris === 'TIM' ? '#2563eb' : '#b45309');
      onChanged?.();
    } catch (e) { done(id, e.response?.data?.detail || 'Errore', '#ef4444'); }
    setBusy(null);
  };
  const verifyCop = async () => {
    setBusy('cop'); const id = start('copertura', label);
    try {
      const r = await axios.post(`${API}/numeri/${cur.id}/copertura`);
      setCur((c) => ({ ...c, ...r.data }));
      const e = r.data.copertura_esito;
      done(id, e === 'COPERTO' ? `Copertura: ${r.data.copertura_tecnologia || 'sì'}` : (e || 'verificata'),
        e === 'COPERTO' ? '#16a34a' : '#64748b');
      onChanged?.();
    } catch (e) { done(id, e.response?.data?.detail || 'Errore', '#ef4444'); }
    setBusy(null);
  };
  const verifySsm = async () => {
    setBusy('ssm'); const id = start('ssmate', label);
    try {
      const r = await axios.post(`${API}/ssmate/verifica/${cur.id}`);
      if (r.data.success) {
        setCur((c) => ({ ...c, ssmate_lucchetto: r.data.lucchetto, ssmate_lavorabile: r.data.lavorabile, ssmate_consistenza: r.data.consistenza, ssmate_presidio: r.data.presidio }));
        done(id, `${r.data.lucchetto}${r.data.lavorabile ? ' · lavorabile' : ' · non lavorabile'}`, r.data.lavorabile ? '#16a34a' : '#b91c1c');
        onChanged?.();
      } else if (r.data.quota_exhausted) {
        const msg = `Crediti SSMATE esauriti — si azzerano alle ${r.data.reset || '02:00'}`;
        done(id, msg, '#b45309'); toast.warning(msg);
      } else if (r.data.queued) { done(id, 'In coda — retry automatico', '#b45309'); }
      else { done(id, r.data.message || 'Non riuscita', '#b45309'); }
    } catch (e) { done(id, e.response?.data?.detail || 'Errore', '#ef4444'); }
    setBusy(null);
  };

  return createPortal(
    <div className="m-portal" data-theme={theme} data-mode={mode}>
      <div className="m-sheet-bg" onClick={onClose} />
      <div className="m-sheet" role="dialog" aria-label="Dettaglio numero">
        <div className="grip" />
        <div className="sh-scroll">
          <div className="sh-head">
            <div className="sh-nome">{nome}</div>
            <div className="sh-num m-mono">{cur.numero_pulito}</div>
            {cur.comune && <div className="sh-loc">{cur.comune}{cur.provincia_prefisso ? ` (${cur.provincia_prefisso})` : ''}</div>}
          </div>

          <div className="sh-rows">
            {esito && <div className="dl"><span>Esito 456</span><b style={{ color: esitoVar, display: 'inline-flex', alignItems: 'center', gap: 7 }}>{esito}{can('gea') && cur.gea_risultato === 'VODAFONE' && <VodafoneBadge size={16} />}</b></div>}
            {cur.partita_iva && <div className="dl"><span>P. IVA</span><b className="m-mono">{cur.partita_iva}</b></div>}
            {coperturaOn && cur.copertura_esito && (
              <div className="dl"><span>Copertura</span>
                <b style={{ color: 'var(--gold)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {cur.copertura_new && <span className="m-new">NEW</span>}
                  {cur.copertura_esito === 'COPERTO' ? (cur.copertura_tecnologia || 'COPERTO') : cur.copertura_esito}
                  {cur.copertura_down ? ` · ${cur.copertura_down} Mbps` : ''}
                </b></div>
            )}
            {can('ssmate') && cur.ssmate_lucchetto && (
              <>
                <div className="dl"><span>SSMATE</span>
                  <b style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: LUCCHETTO_M[cur.ssmate_lucchetto] || '#94a3b8', display: 'inline-block' }} />
                    {cur.ssmate_lucchetto}
                  </b></div>
                <div className="dl"><span>Lavorabile</span><b style={{ color: cur.ssmate_lavorabile ? 'var(--ok)' : 'var(--alert)' }}>{cur.ssmate_lavorabile ? 'Sì' : 'No'}</b></div>
                {cur.ssmate_consistenza && <div className="dl"><span>Consistenza</span><b>{cur.ssmate_consistenza}</b></div>}
              </>
            )}
          </div>

          <div className="sh-rec">
            <div className="sh-lbl">Verifiche</div>
            <div className="m-spie">
              {can('verifiche') && (
                <button className={`m-spia ag456${da456 ? ' on' : ''}`} disabled={!da456 || !!busy} onClick={verify456}
                  title={da456 ? 'Verifica 456' : 'Verificato di recente (timing)'}>
                  {busy === '456' ? <IconSpinner size={18} /> : <PhoneIcon />}
                </button>
              )}
              {coperturaOn && (
                <button className={`m-spia agcop${daCop ? ' on' : ''}`} disabled={!daCop || !!busy} onClick={verifyCop}
                  title={daCop ? 'Verifica copertura' : 'Verificata di recente (timing)'}>
                  {busy === 'cop' ? <IconSpinner size={18} /> : <IconSignal size={18} />}
                </button>
              )}
              {can('ssmate') && (
                <button className={`m-spia agssm${daSsm ? ' on' : ''}`} disabled={!daSsm || !!busy} onClick={verifySsm}
                  title={!cfOk ? 'CF mancante o non valido' : (daSsm ? 'Verifica SSMATE' : 'Verificato di recente (timing)')}>
                  {busy === 'ssm' ? <IconSpinner size={18} /> : <IconBuilding size={18} />}
                </button>
              )}
              {can('gea') && (daGea || busy === 'gea' || cur.gea_risultato === 'VODAFONE') && (
                <button className={`m-spia${cur.gea_risultato === 'VODAFONE' ? ' on agvf' : ''}`} disabled={!daGea || !!busy} onClick={verifyGea}
                  title={cur.gea_risultato === 'VODAFONE' ? 'Linea Vodafone' : (daGea ? 'Verifica Vodafone' : 'Verificato di recente')}>
                  {busy === 'gea' ? <IconSpinner size={18} /> : <VodafoneBadge size={18} style={cur.gea_risultato === 'VODAFONE' ? undefined : { filter: 'grayscale(1)', opacity: 0.55 }} />}
                </button>
              )}
            </div>
          </div>

          {can('ssmate') && cur.ssmate_lucchetto && (
            <div className="sh-rec">
              <div className="sh-lbl">Schermata SSMATE</div>
              <a className="m-rec" href={mediaUrl(`/ssmate/screenshot/${cur.id}`)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <span className="ic">🖼</span><span className="tx">Apri la schermata TIM</span><span className="ar">›</span>
              </a>
            </div>
          )}

          <div className="sh-rec">
            <div className="sh-lbl">Registrazione 456</div>
            {cur.audio_file ? (
              <button className="m-rec" onClick={() => setPlay(true)}>
                <span className="ic"><PlaySmall /></span>
                <span className="tx">Ascolta la registrazione</span>
                <span className="ar">›</span>
              </button>
            ) : (
              <div className="m-sub" style={{ padding: '8px 0' }}>Registrazione non disponibile per questo numero.</div>
            )}
          </div>
        </div>

        <div className="sh-actions">
          <a className="m-btn start" href={`tel:${cur.numero_pulito}`} style={{ textDecoration: 'none' }}><PhoneIcon /> Chiama</a>
          <button className="m-btn ghost" onClick={onClose}><XIcon /> Chiudi</button>
        </div>
      </div>

      {play && (
        <AudioPlayer
          src={mediaUrl(`/audio/${cur.audio_file}`)}
          titolo={cur.numero_pulito}
          sottotitolo={nome}
          onClose={() => setPlay(false)}
        />
      )}
    </div>,
    document.body
  );
}

function FiltriSheet({ filtri, onApply, onClose, onShare, coperturaOn, province, sources }) {
  const { theme, mode } = useContext(MobileTheme);
  const [d, setD] = useState(filtri);
  return createPortal(
    <div className="m-portal" data-theme={theme} data-mode={mode}>
      <div className="m-sheet-bg" onClick={onClose} />
      <div className="m-sheet" role="dialog" aria-label="Filtri">
        <div className="grip" />
        <div className="sh-scroll">
          <div className="f-title">Filtri</div>

          <div className="f-lbl">Cerca</div>
          <input className="f-input" value={d.q} placeholder="Nome, numero o comune"
            onChange={(e) => setD({ ...d, q: e.target.value })} />

          <div className="f-lbl">Tipo impresa</div>
          <div className="m-seg">
            {TIPI.map(([v, l]) => (
              <button key={v} className={d.tipo === v ? 'on' : ''} onClick={() => setD({ ...d, tipo: v })}>{l}</button>
            ))}
          </div>

          <div className="f-lbl">Lavorazione</div>
          <div className="m-seg">
            <button className={!d.lavorazione ? 'on' : ''} onClick={() => setD({ ...d, lavorazione: '' })}>Tutti</button>
            <button className={d.lavorazione === 'da_lavorare' ? 'on' : ''} onClick={() => setD({ ...d, lavorazione: 'da_lavorare' })}>Da lavorare</button>
            <button className={d.lavorazione === 'lavorato' ? 'on' : ''} onClick={() => setD({ ...d, lavorazione: 'lavorato' })}>Lavorati</button>
          </div>
          {coperturaOn && (<>
            <div className="f-lbl">Lead fibra</div>
            <div className="m-seg">
              <button className={!d.lead ? 'on' : ''} onClick={() => setD({ ...d, lead: false })}>Tutti</button>
              <button className={d.lead ? 'on' : ''} onClick={() => setD({ ...d, lead: true })}>Solo lead</button>
            </div>
          </>)}
          <div className="f-lbl">Provincia</div>
          <select className="f-input" value={d.provincia} onChange={(e) => setD({ ...d, provincia: e.target.value })}>
            <option value="">Tutte</option>
            {province.map((p) => (
              <option key={p.nome} value={p.nome}>{p.nome}{p.count != null ? ` (${p.count})` : ''}</option>
            ))}
          </select>

          <div className="f-lbl">Ri-verifica</div>
          <div className="m-seg">
            <button className={!d.scaduti ? 'on' : ''} onClick={() => setD({ ...d, scaduti: false })}>Tutti</button>
            <button className={d.scaduti ? 'on' : ''} onClick={() => setD({ ...d, scaduti: true })}>Da ri-verificare</button>
          </div>

          {coperturaOn && (
            <>
              <div className="f-lbl">Tecnologia copertura</div>
              <div className="m-seg wrap">
                {TECS.map(([v, l]) => (
                  <button key={v} className={d.tec === v ? 'on' : ''} onClick={() => setD({ ...d, tec: v })}>{l}</button>
                ))}
              </div>
              <div className="f-lbl">Esito copertura</div>
              <div className="m-seg wrap">
                {ESITI_COP.map(([v, l]) => (
                  <button key={v} className={d.esitoCop === v ? 'on' : ''} onClick={() => setD({ ...d, esitoCop: v })}>{l}</button>
                ))}
              </div>
              <div className="f-lbl">Diventati FTTH</div>
              <div className="m-seg">
                <button className={!d.ftth ? 'on' : ''} onClick={() => setD({ ...d, ftth: false })}>Tutti</button>
                <button className={d.ftth ? 'on' : ''} onClick={() => setD({ ...d, ftth: true })}>Solo NEW</button>
              </div>
            </>
          )}

          <div className="f-lbl">Lista di origine</div>
          <select className="f-input" value={d.source || ''} onChange={(e) => setD({ ...d, source: e.target.value })}>
            <option value="">Tutte</option>
            {(sources || []).map((s) => <option key={s.nome} value={s.nome}>{s.nome} ({s.count})</option>)}
          </select>

          <div className="f-lbl">Completezza indirizzo</div>
          <select className="f-input" value={d.indirizzo || ''} onChange={(e) => setD({ ...d, indirizzo: e.target.value })}>
            <option value="">Tutti</option>
            <option value="completo">Completo</option>
            <option value="incompleto">Incompleto</option>
            <option value="manca_civico">Manca civico</option>
            <option value="manca_via">Manca via</option>
            <option value="senza_indirizzo">Senza indirizzo</option>
          </select>

          <div className="f-lbl">Stato export</div>
          <select className="f-input" value={d.exportStato || ''} onChange={(e) => setD({ ...d, exportStato: e.target.value })}>
            <option value="">Tutti</option>
            <option value="esportati">Esportati</option>
            <option value="mai">Mai esportati</option>
          </select>

          <div className="f-lbl">TIM inattivo</div>
          <div className="m-seg">
            <button className={!d.timInattivo ? 'on' : ''} onClick={() => setD({ ...d, timInattivo: false })}>Tutti</button>
            <button className={d.timInattivo ? 'on' : ''} onClick={() => setD({ ...d, timInattivo: true })}>Solo inattivi</button>
          </div>

          <button className="m-rec" style={{ marginTop: 16 }} onClick={onShare}>
            <span className="ic"><svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M8 7l4-4 4 4M5 21h14" /></svg></span><span className="tx">Condividi questa lista</span><span className="ar">›</span>
          </button>
        </div>

        <div className="sh-actions">
          <button className="m-btn ghost" onClick={() => setD({ q: '', tipo: '', tec: '', esitoCop: '', provincia: '', scaduti: false, ftth: false, source: '', indirizzo: '', exportStato: '', timInattivo: false, lavorazione: '', lead: false })}>Azzera</button>
          <button className="m-btn start" onClick={() => onApply(d)}>Applica</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ListePage() {
  const { can } = useAuth();
  const setFilterReg = useContext(FilterBarContext);
  const coperturaOn = can('agente_copertura');
  const [data, setData] = useState(null);   // {items,total}
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);     // numero aperto in dettaglio
  const [filtri, setFiltri] = useState({ q: '', tipo: '', tec: '', esitoCop: '', provincia: '', scaduti: false, ftth: false, source: '', indirizzo: '', exportStato: '', timInattivo: false, lavorazione: '', lead: false });
  const [showFilt, setShowFilt] = useState(false);
  const [province, setProvince] = useState([]);
  const [sources, setSources] = useState([]);
  const [timings, setTimings] = useState({ v456: 180, cop: 90, ssm: 90, ssmEnabled: true, gea: 180 });
  const locc = useLocation();
  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setTimings((t) => ({ ...t, v456: r.data?.timing_riverifica_giorni || 180, cop: r.data?.copertura_timing_giorni || 90, gea: r.data?.gea_timing_giorni || 180 }))).catch(() => {});
    if (can('ssmate')) axios.get(`${API}/ssmate/config`).then((r) => setTimings((t) => ({ ...t, ssm: r.data?.timing_giorni || 90, ssmEnabled: r.data?.timing_enabled !== false }))).catch(() => {});
    axios.get(`${API}/dashboard/aree-counts`).then((r) => setProvince(r.data?.province || [])).catch(() => {});
    axios.get(`${API}/numeri/sources`).then((r) => setSources(r.data?.sources || [])).catch(() => {});
  }, []);  // eslint-disable-line
  // pre-filtro "Diventati FTTH" da /liste?ftth=1 (link dall'alert Stato)
  useEffect(() => {
    if (new URLSearchParams(locc.search).get('ftth') === '1') setFiltri((f) => ({ ...f, ftth: true }));
  }, [locc.search]);

  const load = useCallback(() => {
    setLoading(true);
    let url = `${API}/numeri?limit=100&sort=imported_at&order=desc`;
    if (filtri.q) url += `&q=${encodeURIComponent(filtri.q)}`;
    if (filtri.tipo) url += `&tipo_impresa=${filtri.tipo}`;
    if (filtri.tec) {
      const [tec, down] = filtri.tec.split(':');
      url += `&copertura_tecnologia=${tec}`;
      if (down === '10000') url += '&copertura_down_min=10000';
      else if (down === '2500') url += '&copertura_down_min=2500&copertura_down_max=10000';
      else if (down === '1000') url += '&copertura_down_min=1000&copertura_down_max=2500';
    }
    if (filtri.esitoCop) url += `&copertura_esito=${encodeURIComponent(filtri.esitoCop)}`;
    if (filtri.provincia) url += `&provincia=${encodeURIComponent(filtri.provincia)}`;
    if (filtri.scaduti) url += '&scaduti=true';
    if (filtri.ftth) url += '&copertura_new=true';
    if (filtri.source) url += `&source=${encodeURIComponent(filtri.source)}`;
    if (filtri.indirizzo) url += `&indirizzo_stato=${encodeURIComponent(filtri.indirizzo)}`;
    if (filtri.exportStato) url += `&export_stato=${encodeURIComponent(filtri.exportStato)}`;
    if (filtri.timInattivo) url += '&tim_inattivo=true';
    if (filtri.lavorazione) url += `&lavorazione=${filtri.lavorazione}`;
    if (filtri.lead) url += '&lead=true';
    axios.get(url)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [filtri.q, filtri.tipo, filtri.tec, filtri.esitoCop, filtri.provincia, filtri.scaduti, filtri.ftth, filtri.source, filtri.indirizzo, filtri.exportStato, filtri.timInattivo, filtri.lavorazione, filtri.lead]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const nFiltri = (filtri.q ? 1 : 0) + (filtri.tipo ? 1 : 0) + (filtri.tec ? 1 : 0)
    + (filtri.esitoCop ? 1 : 0) + (filtri.provincia ? 1 : 0) + (filtri.scaduti ? 1 : 0) + (filtri.ftth ? 1 : 0)
    + (filtri.source ? 1 : 0) + (filtri.indirizzo ? 1 : 0) + (filtri.exportStato ? 1 : 0) + (filtri.timInattivo ? 1 : 0)
    + (filtri.lavorazione ? 1 : 0) + (filtri.lead ? 1 : 0);

  // registra il tasto Filtri nella top bar (compare solo mentre questa pagina è montata)
  useEffect(() => {
    setFilterReg({ onOpen: () => setShowFilt(true), count: nFiltri });
    return () => setFilterReg(null);
  }, [nFiltri, setFilterReg]);

  const condividi = async () => {
    if (!items.length) { toast.info('Nessun numero da condividere'); return; }
    const righe = items.map((n) => {
      const nome = n.ragione_sociale || 'Senza nome';
      const luogo = n.comune ? ` (${n.comune})` : '';
      const tec = n.copertura_tecnologia ? ` [${n.copertura_tecnologia}]` : '';
      return `${nome} — ${n.numero_pulito || ''}${luogo}${tec}`;
    });
    const testo = `Numeri · ${items.length}\n\n${righe.join('\n')}`;
    try {
      if (navigator.share) await navigator.share({ title: 'TIM456 · Numeri', text: testo });
      else { await navigator.clipboard.writeText(testo); toast.success('Lista copiata negli appunti'); }
    } catch { /* annullato */ }
  };

  return (
    <>
      <div className="m-listhead">
        <span className="lt">Numeri</span>
        <span className="lc">{nf(items.length)}{nFiltri > 0 ? ' filtrati' : ' totali'}</span>
      </div>

      {loading && [0, 1, 2, 3].map((i) => <div key={i} className="m-skel" />)}

      {!loading && items.length === 0 && (
        <div className="m-empty">Nessun numero{nFiltri > 0 ? ' con questi filtri' : ' in questa lista'}.</div>
      )}

      {!loading && items.map((n) => {
        const nome = n.ragione_sociale || 'Senza nome';
        const tec = techLabel(n.copertura_tecnologia);
        return (
          <div className="m-card tap" key={n.id} onClick={() => setSel(n)}>
            <span className="av">{(nome[0] || '#').toUpperCase()}</span>
            <div className="info">
              <div className="nm">{nome}</div>
              <div className="meta">
                {n.risultato && <i className="ed" style={{ background: esitoColor(n.risultato) }} />}
                {n.numero_pulito}{n.comune ? ` · ${n.comune}` : ''}
              </div>
            </div>
            {n.copertura_new && <span className="m-new">NEW</span>}
            {can('gea') && n.gea_risultato === 'VODAFONE' && <VodafoneBadge size={14} />}
            {coperturaOn && tec && <span className={`m-tech ${techClass(n.copertura_tecnologia)}`}>{tec}</span>}
            <span className="det" aria-hidden>›</span>
          </div>
        );
      })}

      {showFilt && (
        <FiltriSheet
          filtri={filtri}
          coperturaOn={coperturaOn}
          province={province}
          sources={sources}
          onApply={(d) => { setFiltri(d); setShowFilt(false); }}
          onClose={() => setShowFilt(false)}
          onShare={() => { setShowFilt(false); condividi(); }}
        />
      )}

      {sel && <Dettaglio n={sel} coperturaOn={coperturaOn} timings={timings} onChanged={load} onClose={() => setSel(null)} />}
    </>
  );
}
